/**
 * Bridge hook — adapts `useLibrarySelection` state into the callback shape
 * `useLibraryDragAndDrop` expects.
 *
 * Origin: architect-strategist BLOCKING finding against
 * `insurescope-zkolv.1.2.6`. The two adapter callbacks (selectedItems →
 * ILibraryDragItem[] and id-set → RowSelectionState) previously lived inline
 * in `DocumentList.tsx`, which coupled the JSX orchestrator to both hooks'
 * private vocabularies and violated the per-hook SRP boundary.
 *
 * This file owns ONLY the conversion. It is a hook (not a pure function) so
 * the `useCallback` references stay stable across renders — `useLibraryDrag-
 * AndDrop` reads them inside pragmatic-dnd's monitor setup and a fresh fn on
 * every render would churn that listener.
 */
import { useCallback, useMemo } from "react";

import type { ILibraryListItem } from "@/api";
import type { ILibraryDragItem } from "@/components/shared/document-picker/DocumentPicker.types";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";

import {
    isForeignSharedSubject,
    isProtectedRootListItem,
} from "../utils/folderTreeUtils";
import { libraryItemRowId } from "../utils/libraryRowId";
import type { ILibraryRow } from "../components/documentListColumns.types";

export interface UseLibrarySelectionDndBridgeOptions {
    rows: ILibraryRow[];
    selectedItems: ILibraryListItem[];
    clearSelection: () => void;
    onRowSelectionChange: OnChangeFn<RowSelectionState>;
}

export interface UseLibrarySelectionDndBridgeResult {
    getSelectedDragItems: () => ILibraryDragItem[];
    isItemSelected: (id: string) => boolean;
    setSelectionFromIds: (ids: Set<string>) => void;
}

const libraryItemToDragItem = (item: ILibraryListItem): ILibraryDragItem => ({
    id: libraryItemRowId(item),
    type: item.type,
    numericId: item.id,
    name: item.name,
    libraryFolderId: item.library_folder_id,
    isKnowledgeBase: item.is_knowledge_base ?? false,
    parentId: item.parent_id,
    isForeignShared: isForeignSharedSubject(item),
});

export function useLibrarySelectionDndBridge({
    rows,
    selectedItems,
    clearSelection,
    onRowSelectionChange,
}: UseLibrarySelectionDndBridgeOptions): UseLibrarySelectionDndBridgeResult {
    const selectableIds = useMemo(() => {
        const ids = new Set(
            selectedItems
                .filter((item) => !isForeignSharedSubject(item))
                .map(libraryItemRowId)
        );
        for (const row of rows) {
            const id = libraryItemRowId(row._raw);
            if (isForeignSharedSubject(row._raw)) {
                ids.delete(id);
            } else {
                ids.add(id);
            }
        }
        return ids;
    }, [rows, selectedItems]);

    // A protected root is never a movable subject, so dragging an ordinary row
    // selected beside one would strand that row too. Dropping them here keeps
    // the rest of the selection draggable; a direct drag of a protected root
    // then falls through to the single source item in `getInitialData`, where
    // dropValidation still refuses it.
    const getSelectedDragItems = useCallback(
        (): ILibraryDragItem[] =>
            selectedItems
                .filter(
                    (item) =>
                        selectableIds.has(libraryItemRowId(item)) &&
                        !isProtectedRootListItem(item)
                )
                .map(libraryItemToDragItem),
        [selectedItems, selectableIds]
    );

    const isItemSelected = useCallback(
        (id: string): boolean =>
            selectableIds.has(id) &&
            selectedItems.some(
                (item) =>
                    !isForeignSharedSubject(item) &&
                    libraryItemRowId(item) === id
            ),
        [selectedItems, selectableIds]
    );

    const setSelectionFromIds = useCallback(
        (ids: Set<string>) => {
            if (ids.size === 0) {
                clearSelection();
                return;
            }
            const next: Record<string, boolean> = {};
            for (const id of ids) {
                if (selectableIds.has(id)) next[id] = true;
            }
            if (Object.keys(next).length > 0) onRowSelectionChange(next);
        },
        [onRowSelectionChange, clearSelection, selectableIds]
    );

    return { getSelectedDragItems, isItemSelected, setSelectionFromIds };
}
